import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { EditorCursorPopup } from "../../../popup";
import { AiPopup } from "../AiPopup/AiPopup";
import { closeAiPopup } from "../../plugins/ai-plugin";
import { useOnClickOutside } from "@smartsuite/react-ui/lib/hooks/onClickOutside/useOnClickOutside";
import { useAiPopup } from "../../hooks/useAiPopup";
import { AiPopupActions, AiPopupState, } from "../../types";
import { serializeNodeToMarkdown } from "../../../../helpers";
import { useElementSize } from "../../../../core/hooks/useElementSize";
import { createAppendSelectionAction, createClosePopupAction, createDiscardChangesAction, createPromptAction, createReplaceInputAction, createReplaceSelectionAction, } from "./AiPopupPlugin.actions";
export const AiPopupPluginView = ({ view, service, }) => {
    const aiPopupRef = useRef();
    const { isVisible, popupState, handlePrompt, loadingResponse, currentResult, error, results, navigateHistoryRight, navigateHistoryLeft, } = useAiPopup({
        view,
        service,
    });
    const [setParentRef, size] = useElementSize(view.dom);
    const aiCommands = useMemo(() => service.getCommands(), [service]);
    const aiGroupLabels = useMemo(() => service.getGroupLabels(), [service]);
    useEffect(() => {
        setParentRef(view.dom);
    }, [view.dom, setParentRef]);
    const handleClosePopup = useCallback(() => {
        closeAiPopup(view);
    }, [view]);
    const getCommandInput = useCallback(() => {
        const handlers = {
            [AiPopupState.EmptyDocument]: () => "",
            [AiPopupState.EmptySelection]: () => {
                return view.state.doc.textBetween(0, view.state.doc.content.size, "\n");
            },
            [AiPopupState.ContentSelected]: () => {
                const { from, to } = view.state.selection;
                const selectedContent = view.state.doc.cut(from, to);
                const markdown = serializeNodeToMarkdown(selectedContent, view.state.schema);
                return markdown;
                // return view.state.doc.textBetween(from, to, "\n");
            },
            [AiPopupState.ContentGenerated]: () => {
                return currentResult.fullResponse;
                // return currentResult.html;
            },
            [AiPopupState.InlineContentGenerated]: () => {
                const { from, to } = view.state.selection;
                const selectedContent = view.state.doc.cut(from, to);
                const markdown = serializeNodeToMarkdown(selectedContent, view.state.schema);
                return markdown;
                // return view.state.doc.textBetween(from, to, "\n");
            },
        };
        return handlers[popupState]();
    }, [popupState, view, currentResult]);
    const handleRunCommand = useCallback(({ command, inputValue, inputElement, setInputValue, option, }) => {
        const commandAction = command.action || AiPopupActions.Prompt;
        const commandHasOptions = command.options && command.options.length > 0;
        if (commandHasOptions && !option)
            return;
        const handlers = {
            [AiPopupActions.Prompt]: createPromptAction({
                command,
                inputValue,
                getCommandInput,
                handlePrompt,
                option,
            }),
            [AiPopupActions.ReplaceInput]: createReplaceInputAction({
                command,
                setValue: setInputValue,
                inputElement,
            }),
            [AiPopupActions.ClosePopup]: createClosePopupAction({
                view,
                handleClosePopup,
            }),
            [AiPopupActions.ReplaceSelection]: createReplaceSelectionAction({
                view,
                currentResult,
                handleClosePopup,
            }),
            [AiPopupActions.AppendSelection]: createAppendSelectionAction({
                view,
                currentResult,
                handleClosePopup,
            }),
            [AiPopupActions.DiscardChanges]: createDiscardChangesAction({
                view,
                popupState,
                handleClosePopup,
            }),
        };
        handlers[commandAction]();
    }, [
        handleClosePopup,
        handlePrompt,
        getCommandInput,
        view,
        currentResult,
        popupState,
    ]);
    const handleInputPrompt = useCallback((inputValue) => {
        var _a, _b;
        const input = getCommandInput();
        const hasContentSelected = [
            AiPopupState.ContentGenerated,
            AiPopupState.ContentSelected,
        ].includes(popupState);
        const appendContext = hasContentSelected ||
            ((_b = (_a = service === null || service === void 0 ? void 0 : service.appendContextOnPromptInput) === null || _a === void 0 ? void 0 : _a.call(service)) !== null && _b !== void 0 ? _b : false);
        const formattedInput = input.trim() !== "" && appendContext ? `""${input}""\n` : "";
        const fullPrompt = `${inputValue} ${formattedInput}`;
        handlePrompt(fullPrompt);
    }, [handlePrompt, getCommandInput, service, popupState]);
    useOnClickOutside(useMemo(() => [aiPopupRef], []), useCallback(() => {
        handleClosePopup();
    }, [handleClosePopup]));
    if (!isVisible) {
        return null;
    }
    const { selection } = view.state;
    const from = selection.$from.depth > 0 ? selection.$from.before() : 0;
    const to = selection.to;
    return (React.createElement(EditorCursorPopup, { view: view, from: from, to: to, placement: "bottom-start", flipEnabled: true, offsetTop: 6, zIndex: 1001 },
        React.createElement("div", { className: "ai-popup-wrapper", style: {
                "--ai-popup-width": `${size.width * 0.95}px`,
            }, ref: aiPopupRef },
            React.createElement(AiPopup, { onClose: handleClosePopup, onPrompt: handleInputPrompt, loading: loadingResponse, state: popupState, commands: aiCommands, groupLabels: aiGroupLabels, onRunCommand: handleRunCommand, currentResult: currentResult, error: error, results: results, navigateHistoryLeft: navigateHistoryLeft, navigateHistoryRight: navigateHistoryRight, keyEvent: service === null || service === void 0 ? void 0 : service.popupKeyEvent, getAiUsageParams: service === null || service === void 0 ? void 0 : service.getUsageParams, popupRef: aiPopupRef }))));
};
